import {
	Box,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Spinner,
	Text,
} from "@chakra-ui/react";
import { Button } from "newui/components/Button";
import { useCurrentCompanyId } from "hooks/auth";
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from "../hooks/useNotifications";
import type { UserNotificationResource } from "../types/notification-centre.types";
import { navigateToDeepLink } from "../utils/navigateToDeepLink";

export interface NotificationCentrePanelProps {
	isOpen: boolean;
	onClose: () => void;
}

const REDACTED_TITLE = "This notification is no longer available";

// AC6: deep_link is an opaque, server-supplied target — not one of this
// app's known client routes — so useNavigationPaths()'s typed route
// builders don't apply; a full navigation is used instead.
//
// VT-023: deep_link is a BE-supplied, ID-based, company-agnostic route (e.g.
// "/rfx/519/notifications"), but the Account/RFx router tree is mounted under
// "/account/:companyId/*", so navigating to the raw path 404s. Prepend the
// active company segment unless the link is already company-scoped or isn't a
// leading-slash relative path (e.g. an absolute/off-account URL).
const buildDeepLinkHref = (deepLink: string, companyId: number | null): string => {
	const isRelativePath = deepLink.startsWith("/");
	const isAlreadyCompanyScoped = deepLink.startsWith("/account/");

	if (isRelativePath && !isAlreadyCompanyScoped && companyId != null) {
		return `/account/${companyId}${deepLink}`;
	}

	return deepLink;
};

const useHandleDeepLink = () => {
	const companyId = useCurrentCompanyId();

	return (deepLink: string | null) => {
		if (!deepLink) {
			return;
		}
		navigateToDeepLink(buildDeepLinkHref(deepLink, companyId));
	};
};

interface NotificationRowProps {
	notification: UserNotificationResource;
	onOpen: (notification: UserNotificationResource) => void;
}

// AC4/§5.3: this renders the SINGLE redaction tier the 112 API can still
// surface as a row — the redacted stub (ordinary-loss, still counted). The
// other tier (hidden-project) never reaches this component at all: those
// rows are absent from the API response entirely, so there is no "hidden"
// branch to render here — absence IS the correct rendering.
const NotificationRow = ({ notification, onOpen }: NotificationRowProps) => {
	const isUnread = notification.read_at === null;

	return (
		<Box
			data-testid={`notification-row-${notification.id}`}
			py={3}
			borderBottomWidth="1px"
			bg={isUnread ? "blue.50" : "white"}
			cursor={notification.is_redacted ? "default" : "pointer"}
			onClick={() => onOpen(notification)}
		>
			<Text fontWeight={isUnread ? "bold" : "normal"} data-testid={`notification-title-${notification.id}`}>
				{notification.is_redacted ? REDACTED_TITLE : notification.title}
			</Text>
			{!notification.is_redacted && notification.body ? (
				<Text fontSize="sm" color="gray.500">
					{notification.body}
				</Text>
			) : null}
		</Box>
	);
};

export const NotificationCentrePanel = ({ isOpen, onClose }: NotificationCentrePanelProps) => {
	const notificationsQuery = useNotifications(isOpen);
	const markReadMutation = useMarkNotificationRead();
	const markAllReadMutation = useMarkAllNotificationsRead();
	const handleDeepLink = useHandleDeepLink();

	const notifications = (notificationsQuery.data?.pages ?? []).flatMap((page) => page.data);

	const handleOpen = (notification: UserNotificationResource) => {
		if (notification.is_redacted) {
			return;
		}
		if (notification.read_at === null) {
			markReadMutation.mutate(notification.id);
		}
		handleDeepLink(notification.deep_link);
	};

	return (
		<Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
			<DrawerOverlay />
			<DrawerContent data-testid="notification-centre-panel">
				<DrawerCloseButton data-testid="notification-centre-close" />
				<DrawerHeader>
					Notifications
					<Button
						btntype="plain"
						size="sm"
						data-testid="notification-centre-read-all"
						onClick={() => markAllReadMutation.mutate()}
					>
						Mark all as read
					</Button>
				</DrawerHeader>
				<DrawerBody>
					{notificationsQuery.isLoading ? (
						<Spinner data-testid="notification-centre-loading" />
					) : null}

					{!notificationsQuery.isLoading && notifications.length === 0 ? (
						<Text data-testid="notification-centre-empty" color="gray.500">
							You have no notifications.
						</Text>
					) : null}

					{notifications.map((notification) => (
						<NotificationRow
							key={notification.id}
							notification={notification}
							onOpen={handleOpen}
						/>
					))}

					{notificationsQuery.hasNextPage ? (
						<Button
							btntype="secondary"
							data-testid="notification-centre-load-more"
							isLoading={notificationsQuery.isFetchingNextPage}
							onClick={() => notificationsQuery.fetchNextPage()}
						>
							Load more
						</Button>
					) : null}
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
